import { StoryBible, StoryBibleEntry } from "../../core/types";
import {
  genId,
  getStoryBibles,
  saveStoryBible,
  deleteStoryBible,
  estimateTokens,
  getSessionsLinkedToStoryBible,
  getSessions,
  saveSession,
} from "../../core/storage";
import { getStrings } from "../../core/i18n";
import { escapeHtml, escapeAttr, infoIcon, wireInfoIcons } from "./domUtils";
import { exportStoryBibleToFile } from "../exportStoryBible";

// Story Bible: what the story actually is (characters, plot, world facts) — independent of any
// provider, optionally linked to a chat (ChatSession.storyBibleId). See CLAUDE.md/types.ts for the
// full design; this file is the UI half:
// - renderStoryBibleList — the "Story Bible" nav-cluster chip's destination, a real top-level
//   screen (taskpane.ts's Screen router) mirroring providerList.ts exactly, per explicit
//   feedback that it should navigate the same way Providers/History do, not stay nested inside
//   the chat screen. Pure CRUD — *editing* a link is not this screen's job (see below), same
//   as Provider list never touches which chat uses which provider either. It does show a
//   read-only "Linked to: ..." line per card (2026-08-13, see linkedChatsLabel below) — display
//   only, no click-through to edit from here; that distinction (show vs. edit) is what keeps
//   this consistent with the original reasoning, not a reversal of it.
// - renderStoryBibleEditor — create/edit one bible. Used two ways, deliberately kept as one
//   shared implementation rather than two: (a) top-level, via taskpane.ts's "storyBibleForm"
//   screen, reached from this list's own Add/Edit; (b) nested inside the chat screen's
//   #chat-content, via the Story Bible field's own Create/Edit buttons on the New chat/Edit
//   session forms (sessionSwitcher.ts's storyBibleFieldHtml) — that path deliberately still
//   returns to a *fresh* New chat/Edit session render afterward (per explicit choice: one shared
//   editor, at the cost of any other unsaved fields in that form being discarded, same as
//   Cancel already discards them today). Also the only place (2026-08-13) that shows/edits a
//   bible's linked chats directly — see the "Linked chats" section inside `render()` below
//   ("bind"/"bound" was the original working term for this feature, renamed to "link"/"linked"
//   the same day, before it ever shipped, per explicit preference — nothing to migrate, just a
//   naming choice). Previously a link could *only* be changed via Edit session's own Story Bible
//   field; that's still true for linking one chat while you're already in it, but managing *all*
//   of a bible's links from the bible's own side (see/remove/add/replace) had no equivalent
//   until this.

// Pure markup for the "Story Bible" field shared by the New chat/Edit session forms — mirrors
// overrideFieldsHtml's idPrefix convention so both screens' element ids stay distinct even
// though they're never on screen at once. Wiring (Create/Edit button clicks, enabling Edit once
// a real bible is selected) is the caller's own — see sessionSwitcher.ts — since navigating to
// the editor and back means re-rendering the whole parent form, which only the parent knows how
// to do (it alone has the provider/model/session state this field's own screen doesn't).
export function storyBibleFieldHtml(idPrefix: string, selectedId: string | undefined): string {
  const s = getStrings();
  const bibles = getStoryBibles();
  return `
    <label class="form-last-field">${s.storyBible.fieldLabel}
      <select id="${idPrefix}-story-bible">
        <option value="">${s.storyBible.none}</option>
        ${bibles
          .map(
            (b) =>
              `<option value="${b.id}"${b.id === selectedId ? " selected" : ""}>${escapeHtml(b.name)}</option>`
          )
          .join("")}
      </select>
    </label>
    <div class="form-actions story-bible-field-actions">
      <button type="button" class="secondary-btn" id="${idPrefix}-story-bible-create-btn">${s.storyBible.create}</button>
      <button type="button" class="secondary-btn" id="${idPrefix}-story-bible-edit-btn"${selectedId ? "" : " disabled"}>${s.storyBible.edit}</button>
    </div>`;
}

// Create/edit one Story Bible: a name plus a growing list of freeform entries (title optional,
// text, an include/exclude toggle for the token-budget concern the design was built around — see
// chat.ts's effectiveProviderConfig, which only sends included entries). Everything here is held
// in local, unsaved state (`name`/`entries`) until Save — matches this app's existing "no draft
// preservation across screen navigation" model (same as Edit session's own Cancel discarding
// typed-but-unsaved edits) rather than introducing a new autosave/draft mechanism. Entry-level
// actions (add/edit/delete/toggle) never touch storage directly; only the outer Save does, via
// one saveStoryBible() call — this mirrors chat.ts's renderMessages(), which rebuilds its own
// section from local/session state on every small interaction rather than persisting each one.
export function renderStoryBibleEditor(
  contentEl: HTMLElement,
  existing: StoryBible | undefined,
  onSaved: (bible: StoryBible) => void,
  onCancel: () => void
): void {
  const s = getStrings();
  let name = existing?.name ?? "";
  let entries: StoryBibleEntry[] = existing ? existing.entries.map((e) => ({ ...e })) : [];
  let entryForm: { mode: "add" } | { mode: "edit"; id: string } | null = null;
  let entryFormTitle = "";
  let entryFormText = "";
  let entriesExpanded = false;
  let error = "";

  // Only included entries count toward what actually gets sent — see effectiveProviderConfig.
  function totalTokens(): number {
    return entries.filter((e) => e.included).reduce((sum, e) => sum + estimateTokens(e.text), 0);
  }

  // The compact preview is a 2-line CSS clamp (.story-bible-entry-text), not a character
  // count — it shrinks/grows with the pane's own width instead of a fixed cutoff. Newlines are
  // collapsed to spaces here (not preserved) so an entry that happens to start with blank lines
  // can never render as an empty-looking preview.
  function entrySnippet(text: string): string {
    return text.replace(/\s*\n+\s*/g, " ").trim();
  }

  function entryMetaHtml(entry: StoryBibleEntry, disabled: boolean): string {
    return `
      <div class="story-bible-entry-meta">
        <span class="story-bible-entry-tokens">${s.storyBible.tokensCount(estimateTokens(entry.text).toLocaleString())}</span>
        <button type="button" class="link-btn" data-action="edit-entry" data-id="${entry.id}"${disabled ? " disabled" : ""}>${s.common.edit}</button>
        <button type="button" class="link-btn" data-action="delete-entry" data-id="${entry.id}"${disabled ? " disabled" : ""}>${s.common.delete}</button>
      </div>`;
  }

  function entryRowHtml(entry: StoryBibleEntry, disabled: boolean, expanded: boolean): string {
    const trimmed = entry.text.trim();
    const bodyHtml = expanded
      ? `<span class="story-bible-entry-text story-bible-entry-text-expanded">${escapeHtml(trimmed)}</span>`
      : `<span class="story-bible-entry-text">${escapeHtml(entrySnippet(trimmed))}</span>`;
    return `
      <li class="story-bible-entry${expanded ? " story-bible-entry-expanded" : ""}${disabled ? " is-locked" : ""}"${disabled ? ` title="${escapeAttr(s.storyBible.pendingEntryNote)}"` : ""}>
        <div class="story-bible-entry-main">
          <input
            type="checkbox"
            class="story-bible-entry-checkbox"
            data-action="toggle-included"
            data-id="${entry.id}"
            ${entry.included ? "checked" : ""}
            ${disabled ? "disabled" : ""}
            aria-label="${entry.included ? s.storyBible.includedAria : s.storyBible.excludedAria}"
            title="${entry.included ? s.storyBible.includedTitle : s.storyBible.excludedTitle}"
          />
          <div class="story-bible-entry-info">
            ${entry.title ? `<strong>${escapeHtml(entry.title)}</strong>` : ""}
            ${bodyHtml}
          </div>
        </div>
        ${entryMetaHtml(entry, disabled)}
      </li>`;
  }

  function entryFormHtml(): string {
    return `
      <div class="story-bible-entry-form">
        <label>${s.storyBible.entryTitle}
          <input type="text" id="bible-entry-title" value="${escapeAttr(entryFormTitle)}" />
        </label>
        <label>${s.storyBible.entryText}
          <textarea id="bible-entry-text" rows="10">${escapeHtml(entryFormText)}</textarea>
        </label>
        <div class="form-actions">
          <button type="button" class="primary-btn" id="bible-entry-save-btn">${s.storyBible.saveEntry}</button>
          <button type="button" class="link-btn" id="bible-entry-cancel-btn">${s.common.cancel}</button>
        </div>
      </div>`;
  }

  // Builds the entry list plus whatever sits directly below it: the "+ Add entry" button, the
  // add-entry form (appended after the list, same spot the button occupied), or — when editing —
  // the edit form spliced in right after that entry's own <li>, not after the whole list, so it
  // opens exactly where the entry being edited is rather than always at the bottom.
  function entriesSectionHtml(): string {
    const entryFormOpen = entryForm !== null;
    const editingId = entryForm?.mode === "edit" ? entryForm.id : null;

    let listHtml: string;
    if (entries.length === 0) {
      listHtml = `<p class="empty-hint">${s.storyBible.noEntries}</p>`;
    } else {
      const rows = entries.map((entry) => {
        const rowHtml = entryRowHtml(entry, entryFormOpen, entriesExpanded);
        if (entry.id === editingId) {
          return `${rowHtml}<li class="story-bible-entry-form-wrap">${entryFormHtml()}</li>`;
        }
        return rowHtml;
      });
      listHtml = `<ul class="story-bible-entries">${rows.join("")}</ul>`;
    }

    const trailingHtml =
      entryForm?.mode === "add"
        ? entryFormHtml()
        : entryFormOpen
          ? ""
          : `<button type="button" class="primary-btn" id="bible-add-entry-btn">${s.storyBible.addEntry}</button>`;

    return `${listHtml}${trailingHtml}`;
  }

  // Only meaningful for an *existing* bible — a brand-new one has no id yet, so nothing could
  // possibly be linked to it. See the top-of-file comment for why this lives here now (2026-08-13)
  // rather than staying purely on the Edit session side.
  function linkedChatsSectionHtml(): string {
    if (!existing) return "";
    const linked = getSessionsLinkedToStoryBible(existing.id);
    const linkedListHtml =
      linked.length === 0
        ? `<p class="empty-hint">${s.storyBible.linkedChatsNone}</p>`
        : `<ul class="story-bible-linked-chats">
            ${linked
              .map(
                (session) => `
              <li class="story-bible-linked-chat">
                <span>${escapeHtml(session.title)}${session.state === "closed" ? ` ${s.storyBible.closedTag}` : ""}</span>
                <button type="button" class="link-btn" data-action="unlink-chat" data-id="${session.id}">${s.storyBible.unlink}</button>
              </li>`
              )
              .join("")}
          </ul>`;

    // Every open chat not already linked to *this* bible — includes both genuinely unlinked ones
    // and ones currently linked to a different bible (picking one of those is how "replace" works;
    // ChatSession.storyBibleId is a single field, so linking here simply overwrites it, which
    // automatically drops it from whatever bible held it before — no separate unlink step needed).
    // Closed chats are deliberately not offered — linking one you're not actively using seemed
    // low-value; reopen it first via History if you do want to link it.
    const bibles = getStoryBibles();
    const linkable = getSessions().filter(
      (session) => session.state === "open" && session.storyBibleId !== existing.id
    );
    const linkOptionsHtml = linkable
      .map((session) => {
        const currentBible = session.storyBibleId
          ? bibles.find((b) => b.id === session.storyBibleId)
          : undefined;
        const label = currentBible
          ? s.storyBible.linkedElsewhereOption(session.title, currentBible.name)
          : s.storyBible.unlinkedOption(session.title);
        return `<option value="${escapeAttr(session.id)}">${escapeHtml(label)}</option>`;
      })
      .join("");

    return `
      <div class="story-bible-linked-chats-section">
        <h3>${s.storyBible.linkedChatsHeading}</h3>
        ${linkedListHtml}
        ${
          linkable.length > 0
            ? `<div class="story-bible-link-row">
                <select id="bible-link-select">
                  <option value="" selected disabled>${s.storyBible.linkSelectPlaceholder}</option>
                  ${linkOptionsHtml}
                </select>
                <button type="button" class="secondary-btn" id="bible-link-btn">${s.storyBible.link}</button>
              </div>`
            : ""
        }
      </div>`;
  }

  function render(): void {
    const entryFormOpen = entryForm !== null;
    contentEl.innerHTML = `
      <div class="screen story-bible-form-screen">
        <div class="screen-header">
          <h2>${existing ? s.storyBible.titleEdit : s.storyBible.titleNew}</h2>
        </div>
        <label>${s.storyBible.nameLabel}
          <input type="text" id="bible-name" value="${escapeAttr(name)}" />
        </label>
        <div class="story-bible-entries-header">
          <span class="label-row"><span>${s.storyBible.entries}</span>${infoIcon(s.storyBible.entriesInfo)}</span>
          <span class="story-bible-token-total">${s.storyBible.tokensTotal(totalTokens().toLocaleString())}</span>
        </div>
        ${
          entries.length > 0
            ? `<div class="story-bible-entries-toolbar">
                <button type="button" class="link-btn" id="bible-expand-toggle-btn"${entryFormOpen ? " disabled" : ""}>${entriesExpanded ? s.storyBible.collapseAll : s.storyBible.expandAll}</button>
              </div>`
            : ""
        }
        ${entriesSectionHtml()}
        ${entryFormOpen ? `<p class="pending-entry-note">${s.storyBible.pendingEntryNote}</p>` : ""}
        ${linkedChatsSectionHtml()}
        <p id="bible-error" class="error-text" style="display:${error ? "" : "none"};">${escapeHtml(error)}</p>
        <div class="form-actions">
          <button type="button" class="primary-btn" id="bible-save-btn"${entryFormOpen ? " disabled" : ""}>${s.common.save}</button>
          <button type="button" class="secondary-btn" id="bible-export-btn"${entryFormOpen ? " disabled" : ""}>${s.common.export}</button>
          <button type="button" class="link-btn" id="bible-cancel-btn">${s.common.cancel}</button>
        </div>
      </div>
    `;

    wireInfoIcons(contentEl);

    const nameInput = contentEl.querySelector("#bible-name") as HTMLInputElement;
    nameInput.addEventListener("input", () => {
      name = nameInput.value;
    });

    contentEl
      .querySelectorAll<HTMLInputElement>('[data-action="toggle-included"]')
      .forEach((checkbox) => {
        checkbox.addEventListener("change", () => {
          const target = entries.find((e) => e.id === checkbox.dataset.id);
          if (target) target.included = checkbox.checked;
          render();
        });
      });

    contentEl.querySelectorAll<HTMLButtonElement>('[data-action="edit-entry"]').forEach((btn) => {
      btn.addEventListener("click", () => {
        const target = entries.find((e) => e.id === btn.dataset.id);
        if (!target) return;
        error = "";
        entryForm = { mode: "edit", id: target.id };
        entryFormTitle = target.title ?? "";
        entryFormText = target.text;
        render();
      });
    });

    contentEl.querySelectorAll<HTMLButtonElement>('[data-action="delete-entry"]').forEach((btn) => {
      btn.addEventListener("click", () => {
        entries = entries.filter((e) => e.id !== btn.dataset.id);
        render();
      });
    });

    contentEl.querySelector("#bible-add-entry-btn")?.addEventListener("click", () => {
      error = "";
      entryForm = { mode: "add" };
      entryFormTitle = "";
      entryFormText = "";
      render();
    });

    contentEl.querySelector("#bible-expand-toggle-btn")?.addEventListener("click", () => {
      entriesExpanded = !entriesExpanded;
      render();
    });

    contentEl.querySelector("#bible-export-btn")?.addEventListener("click", () => {
      exportStoryBibleToFile({
        id: existing?.id ?? genId(),
        name: name.trim() || existing?.name || s.storyBible.titleNew,
        entries,
      });
    });

    // Unlink/Link act on storage directly (not the in-progress `entries`/`name` edit state above,
    // which only gets persisted by Save) — matches how the linked-chats section is read fresh
    // from storage on every render() rather than tracked as its own piece of local state, since
    // there's nothing to "discard on Cancel" here the way there is for entries: each click is
    // already a real, saved change to the *chat's* own record, not a pending edit to this bible.
    contentEl.querySelectorAll<HTMLButtonElement>('[data-action="unlink-chat"]').forEach((btn) => {
      btn.addEventListener("click", () => {
        const session = getSessions().find((sess) => sess.id === btn.dataset.id);
        if (!session) return;
        session.storyBibleId = undefined;
        saveSession(session);
        render();
      });
    });

    contentEl.querySelector("#bible-link-btn")?.addEventListener("click", () => {
      if (!existing) return;
      const select = contentEl.querySelector("#bible-link-select") as HTMLSelectElement | null;
      const sessionId = select?.value;
      if (!sessionId) return;
      const session = getSessions().find((sess) => sess.id === sessionId);
      if (!session) return;
      // Overwriting storyBibleId is the entire "replace" mechanism — a chat can only ever point
      // at one bible, so linking here automatically drops it from whatever bible held it before,
      // with no separate unlink step needed (see linkedChatsSectionHtml's own comment).
      session.storyBibleId = existing.id;
      saveSession(session);
      render();
    });

    const entryTitleInput = contentEl.querySelector(
      "#bible-entry-title"
    ) as HTMLInputElement | null;
    entryTitleInput?.addEventListener("input", () => {
      entryFormTitle = entryTitleInput.value;
    });
    const entryTextInput = contentEl.querySelector(
      "#bible-entry-text"
    ) as HTMLTextAreaElement | null;
    entryTextInput?.addEventListener("input", () => {
      entryFormText = entryTextInput.value;
    });

    contentEl.querySelector("#bible-entry-cancel-btn")?.addEventListener("click", () => {
      entryForm = null;
      render();
    });

    contentEl.querySelector("#bible-entry-save-btn")?.addEventListener("click", () => {
      error = "";
      const title = entryFormTitle.trim();
      // Required (not just recommended) — a title tells the AI what this fact is about at a
      // glance, the same way a heading does, rather than making it infer that from the body text
      // alone on every single entry.
      if (!title) {
        error = s.storyBible.titleRequired;
        render();
        return;
      }
      const text = entryFormText.trim();
      if (!text) {
        error = s.storyBible.entryTextRequired;
        render();
        return;
      }
      const form = entryForm;
      if (form && form.mode === "edit") {
        const target = entries.find((e) => e.id === form.id);
        if (target) {
          target.title = title;
          target.text = text;
        }
      } else {
        entries.push({ id: genId(), title, text, included: true });
      }
      entryForm = null;
      render();
    });

    contentEl.querySelector("#bible-cancel-btn")?.addEventListener("click", onCancel);

    contentEl.querySelector("#bible-save-btn")?.addEventListener("click", () => {
      error = "";
      const trimmedName = name.trim();
      if (!trimmedName) {
        error = s.storyBible.nameRequired;
        render();
        return;
      }
      const bible: StoryBible = {
        id: existing?.id ?? genId(),
        name: trimmedName,
        entries,
      };
      saveStoryBible(bible);
      onSaved(bible);
    });
  }

  render();
}

export interface StoryBibleListHandlers {
  onAdd: () => void;
  onEdit: (bible: StoryBible) => void;
  onBackToChat: () => void;
  onChanged: () => void;
}

// How many linked-chat titles to spell out before collapsing the rest into "+N more" — keeps
// the list card compact regardless of whether a bible has 1 or 30 linked chats. Deliberately not
// a character-length truncation (like providerList.ts's systemPromptSnippet) — cutting mid-name
// would read as broken rather than trimmed for a list of discrete titles.
const LINKED_CHATS_PREVIEW_COUNT = 3;

// Filters to *open* chats only (state === "open") — a closed chat still technically has
// storyBibleId set (getSessionsLinkedToStoryBible returns both), but it isn't currently "in use",
// and mixing open/closed into one compact summary line risked reading as cluttered or implying
// live dependencies that aren't actually live. Closed links aren't hidden from the app entirely
// by this — Edit Session's own dropdown and the Edit Story Bible screen's own section (see
// linkedChatsSectionHtml above) both still surface them; this is just the one compact card line
// staying open-only.
function linkedChatsLabel(bible: StoryBible): string {
  const s = getStrings();
  const openTitles = getSessionsLinkedToStoryBible(bible.id)
    .filter((session) => session.state === "open")
    .map((session) => session.title);
  if (openTitles.length === 0) return s.storyBible.notLinkedToAnyChat;
  const shown = openTitles.slice(0, LINKED_CHATS_PREVIEW_COUNT);
  const moreCount = openTitles.length - shown.length;
  return s.storyBible.linkedToChats(shown, moreCount);
}

// The "Story Bible" chip's screen (chat.ts's nav-cluster row, next to Providers) — pure list/
// manage, mirroring providerList.ts's markup (.provider-items/.provider-item, "+ Add X" button at
// the bottom). Deliberately has no *editing* control of its own for which chats are linked (see
// the read-only "Linked to: ..." line below instead) — this screen (like Provider list) never
// needs to know which chat, if any, is currently active. Editing links happens via Edit Session's
// own field, or (2026-08-13) the Edit Story Bible screen's own "Linked chats" section.
//
// Delete here has no confirmation step, by explicit design: it's the soft half of this app's
// existing two-tier delete model (closing a chat is that model's soft delete; History's own
// Delete is the permanent purge — see storage.ts's deleteStoryBible/ChatSession.state). A bible
// deleted here lands in History's Deleted Story Bibles section, recoverable via Recover, exactly
// like a closed chat is recoverable via Reopen with no confirmation needed either. Only Purge
// (History) is permanent and asks for confirmation.
export function renderStoryBibleList(
  container: HTMLElement,
  handlers: StoryBibleListHandlers
): void {
  const s = getStrings();
  const bibles = getStoryBibles();

  container.innerHTML = `
    <div class="screen provider-list-screen">
      <div class="screen-header">
        <h2>${s.storyBible.listTitle}</h2>
        <button type="button" class="link-btn" data-action="back">${s.common.backToChat}</button>
      </div>
      <div class="screen-list-scroll">
        ${
          bibles.length === 0
            ? `<p class="empty-hint">${s.storyBible.listEmptyHint}</p>`
            : `<ul class="provider-items">
                ${bibles
                  .map((b) => {
                    return `
                  <li class="provider-item">
                    <div class="provider-item-info">
                      <strong>${escapeHtml(b.name)}</strong>
                      <span class="provider-meta">${s.storyBible.entryCount(b.entries.length)}</span>
                      <span class="provider-meta">${escapeHtml(linkedChatsLabel(b))}</span>
                    </div>
                    <div class="provider-item-actions">
                      <div class="actions-row">
                        <button type="button" class="secondary-btn" data-action="edit" data-id="${b.id}">${s.common.edit}</button>
                        <button type="button" class="secondary-btn" data-action="export" data-id="${b.id}">${s.common.export}</button>
                        <button type="button" class="danger-btn" data-action="delete" data-id="${b.id}">${s.common.delete}</button>
                      </div>
                    </div>
                  </li>`;
                  })
                  .join("")}
              </ul>`
        }
      </div>
      <div class="screen-fixed-footer">
        <button type="button" class="primary-btn" id="add-story-bible-btn">${s.storyBible.addStoryBible}</button>
      </div>
    </div>
  `;

  container.querySelector("#add-story-bible-btn")?.addEventListener("click", handlers.onAdd);
  container
    .querySelector('[data-action="back"]')
    ?.addEventListener("click", () => handlers.onBackToChat());

  container.querySelectorAll<HTMLButtonElement>('[data-action="edit"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const bible = bibles.find((b) => b.id === btn.dataset.id);
      if (bible) handlers.onEdit(bible);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="export"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const bible = bibles.find((b) => b.id === btn.dataset.id);
      if (bible) exportStoryBibleToFile(bible);
    });
  });

  container.querySelectorAll<HTMLButtonElement>('[data-action="delete"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) deleteStoryBible(btn.dataset.id);
      handlers.onChanged();
    });
  });
}
